import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import GlobalNav from "@/components/GlobalNav";
import ErrorBoundary from "@/components/ErrorBoundary";
import LandingPage from "@/pages/LandingPageNew";
import ReportsPage from "@/pages/ReportsPage";
import SignupPage from "@/pages/SignupPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import { InstallAppButton } from "@/components/PWAInstallPrompt";
import { MobileAppShell } from "@/components/MobileAppShell";

import { HelmetProvider } from 'react-helmet-async';

import { useState, useEffect, useCallback, Suspense, lazy } from "react";
import React from "react";
import { toast } from "sonner";
import { analytics } from "@/utils/analytics";
import { useLocation } from "react-router-dom";

// SPA Page View Tracker
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    analytics.captureUTM();
    analytics.pageView(location.pathname, document.title);
  }, [location.pathname]);
  return null;
}

// Lazy-loaded pages for code splitting
const EditorPage = lazy(() => import("@/pages/EditorPage"));
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const PrintReportPage = lazy(() => import("@/pages/PrintReportPage"));
const CompassPage = lazy(() => import("@/pages/CompassPage"));
const FloorPlanCreatorPage = lazy(() => import("@/pages/FloorPlanCreatorPage"));
const AstroVastuPage = lazy(() => import("@/pages/AstroVastuPage"));
const PinnacleGuruPage = lazy(() => import("@/pages/PinnacleGuruPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const AstroReportLandingPage = lazy(() => import("@/pages/AstroReportLandingPage"));
const VastuReportLandingPage = lazy(() => import("@/pages/VastuReportLandingPage"));
const NumerologyPage = lazy(() => import("@/pages/NumerologyPage"));
const NumerologyReportPage = lazy(() => import("@/pages/NumerologyReportPage"));
const NumerologyReportLandingPage = lazy(() => import("@/pages/NumerologyReportLandingPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const InstallPage = lazy(() => import("@/pages/InstallPage"));
const GetAppPage = lazy(() => import("@/pages/GetAppPage"));
const ClientCRMPage = lazy(() => import("@/pages/ClientCRMPage"));
const ClientProfilePage = lazy(() => import("@/pages/ClientProfilePage"));
const ClientPortalPage = lazy(() => import("@/pages/ClientPortalPage"));
const CRMReportViewerPage = lazy(() => import("@/pages/CRMReportViewerPage"));
const ConsultationLandingPage = lazy(() => import("@/pages/ConsultationLandingPage"));
const ConsultationSuccessPage = lazy(() => import("@/pages/ConsultationSuccessPage"));
const RemediesPage = lazy(() => import("@/pages/RemediesPage"));
const TutorialsPage = lazy(() => import("@/pages/TutorialsPage"));
const GoPremiumPage = lazy(() => import("@/pages/GoPremiumPage"));
const PremiumPage = lazy(() => import("@/pages/PremiumPage"));
const VastuCoursePage = lazy(() => import("@/pages/VastuCoursePage"));
const VastuSoftwarePage = lazy(() => import("@/pages/VastuSoftwarePage"));
const DevtasVastuPage = lazy(() => import("@/pages/DevtasVastuPage"));
const AcharyaProfilePage = lazy(() => import("@/pages/AcharyaProfilePage"));
const VastuConsultationPage = lazy(() => import("@/pages/VastuConsultationPage"));
const SEOBlogManager = lazy(() => import("@/pages/SEOBlogManager"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogListPage = lazy(() => import("@/pages/BlogListPage"));
const TaoAayCalculator = lazy(() => import("@/pages/TaoAayCalculator"));
const VastuCalculators = lazy(() => import("@/pages/VastuCalculators"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const RefundPolicyPage = lazy(() => import("@/pages/RefundPolicyPage"));
const FreeVastuSoftwarePage = lazy(() => import("@/pages/FreeVastuSoftwarePage"));
const KPAstrologySoftwarePage = lazy(() => import("@/pages/KPAstrologySoftwarePage"));
const FreeNumerologyCalculatorPage = lazy(() => import("@/pages/FreeNumerologyCalculatorPage"));

// Free occult tool pages (SEO + acquisition)
const MangalDoshaToolPage = lazy(() => import("@/pages/tools/MangalDoshaToolPage"));
const KalsarpaToolPage = lazy(() => import("@/pages/tools/KalsarpaToolPage"));
const SadeSatiToolPage = lazy(() => import("@/pages/tools/SadeSatiToolPage"));
const TithiToolPage = lazy(() => import("@/pages/tools/TithiToolPage"));
const NakshatraFinderPage = lazy(() => import("@/pages/tools/NakshatraFinderPage"));
const MuhuratFinderPage = lazy(() => import("@/pages/tools/MuhuratFinderPage"));
const KundliMilanPage = lazy(() => import("@/pages/tools/KundliMilanPage"));
const BabyNamePage = lazy(() => import("@/pages/tools/BabyNamePage"));
const MarriageDatesPage = lazy(() => import("@/pages/tools/MarriageDatesPage"));
const FamilyVaultPage = lazy(() => import("@/pages/FamilyVaultPage"));
const FriendsComparePage = lazy(() => import("@/pages/FriendsComparePage"));

// Phase C tools
const TarotPage = lazy(() => import("@/pages/tools/TarotPage"));
const TarotJournalPage = lazy(() => import("@/pages/tools/TarotJournalPage"));
const TarotSharedPage = lazy(() => import("@/pages/tools/TarotSharedPage"));
const LalKitabPage = lazy(() => import("@/pages/tools/LalKitabPage"));
const NameNumerologyAuditPage = lazy(() => import("@/pages/tools/NameNumerologyAuditPage"));
const VehicleNumeroPage = lazy(() => import("@/pages/tools/VehicleNumeroPage"));
const RudrakshaPage = lazy(() => import("@/pages/tools/RudrakshaPage"));

// Courses module
const CoursesPage = lazy(() => import("@/pages/CoursesPage"));
const CourseDetailPage = lazy(() => import("@/pages/CourseDetailPage"));
const MyCoursesPage = lazy(() => import("@/pages/MyCoursesPage"));

// OK Community (consumes external mobile-app backend)
const CommunityFeedPage = lazy(() => import("@/pages/community/CommunityFeedPage"));
const CommunityPostPage = lazy(() => import("@/pages/community/CommunityPostPage"));
const CommunityAskPage = lazy(() => import("@/pages/community/CommunityAskPage"));
const CommunityLeaderboardPage = lazy(() => import("@/pages/community/CommunityLeaderboardPage"));
const CommunityProfilePage = lazy(() => import("@/pages/community/CommunityProfilePage"));
const CommunitySocialFeedPage = lazy(() => import("@/pages/community/CommunitySocialFeedPage"));
const CommunityDirectoryPage = lazy(() => import("@/pages/community/CommunityDirectoryPage"));

// Premium extras (Tier 1)
const MuhuratCalendarPage = lazy(() => import("@/pages/premium/MuhuratCalendarPage"));
const KarmaLogPage = lazy(() => import("@/pages/premium/KarmaLogPage"));

// Mahakundli (50+ page personalised premium report)
const MahakundliLandingPage = lazy(() => import("@/pages/MahakundliLandingPage"));
// Lal Kitab (60+ page personalised premium report — Pt. Roop Chand Joshi tradition)
const LalKitabLandingPage = lazy(() => import("@/pages/LalKitabLandingPage"));
const BNNLandingPage = lazy(() => import("@/pages/BNNLandingPage"));
const RajYogLandingPage = lazy(() => import("@/pages/RajYogLandingPage"));
const LifeReportLandingPage = lazy(() => import("@/pages/LifeReportLandingPage"));
const PaymentReturnPage = lazy(() => import("@/pages/PaymentReturnPage"));
const PayContinuePage = lazy(() => import("@/pages/PayContinuePage"));
const ResumeCheckoutPage = lazy(() => import("@/pages/ResumeCheckoutPage"));
const VastuReportGatePage = lazy(() => import("@/pages/VastuReportGatePage"));
const AstroVastuReportLandingPage = lazy(() => import("@/pages/AstroVastuReportLandingPage"));
const PalmistryReportLandingPage = lazy(() => import("@/pages/PalmistryReportLandingPage"));
const FaceReadingReportLandingPage = lazy(() => import("@/pages/FaceReadingReportLandingPage"));

// App version for cache busting - this is the base version
// Server returns version with build ID (e.g., "2.3.0-a1b2c3d4") that changes on every deploy
const APP_VERSION = "2.3.0";
const VERSION_CHECK_INTERVAL = 1 * 60 * 1000; // Check every 1 minute for faster update detection

function App() {
  const [user, setUser] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);

  // Force hard refresh and clear cache
  const forceRefresh = useCallback(() => {
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    // Clear localStorage version
    localStorage.setItem('app_version', serverVersion || APP_VERSION);
    localStorage.removeItem('pending_refresh');
    // Force hard reload (bypass cache)
    window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now();
  }, [serverVersion]);

  // Check for app updates by fetching version from server
  const checkForUpdates = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/version?_=${Date.now()}`, {
        cache: 'no-store',
        headers: { 
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Use version_base for comparison to avoid false positives from build ID changes
        const newServerVersion = data.version_base || data.version;
        setServerVersion(newServerVersion);
        const storedVersion = localStorage.getItem('app_version');
        // Extract base version from stored version (remove build ID if present)
        const storedBaseVersion = storedVersion ? storedVersion.split('-')[0] : null;
        
        // Only show update if base version actually changed (e.g., 2.3.0 -> 2.4.0)
        // Don't show for build ID changes within same version
        if (storedBaseVersion && storedBaseVersion !== newServerVersion) {
          console.log(`[VERSION] Update available: ${storedBaseVersion} → ${newServerVersion}`);
          setUpdateAvailable(true);
          
          // Show toast only once per session using sessionStorage flag
          const updateShownThisSession = sessionStorage.getItem('update_toast_shown');
          if (!updateShownThisSession) {
            sessionStorage.setItem('update_toast_shown', 'true');
            toast.info(
              <div className="flex items-center gap-2">
                <span className="text-xl">🔄</span>
                <div>
                  <p className="font-bold">New Update Available!</p>
                  <p className="text-sm opacity-80">v{storedBaseVersion} → v{newServerVersion}</p>
                </div>
              </div>,
              {
                duration: 8000, // Auto-dismiss after 8 seconds
                id: 'version-update-toast',
                action: {
                  label: "Update Now",
                  onClick: () => {
                    localStorage.setItem('app_version', newServerVersion);
                    sessionStorage.removeItem('update_toast_shown');
                    if ('caches' in window) {
                      caches.keys().then(names => {
                        names.forEach(name => caches.delete(name));
                      });
                    }
                    window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now();
                  }
                }
              }
            );
          }
        } else {
          // Versions match or first visit - no update needed
          setUpdateAvailable(false);
          if (!storedVersion) {
            // First visit - store current version
            localStorage.setItem('app_version', newServerVersion);
          } else if (storedBaseVersion === newServerVersion) {
            // Same version, ensure it's updated in localStorage
            localStorage.setItem('app_version', newServerVersion);
          }
        }
      }
    } catch (err) {
      // Silently fail version check - not critical
      console.debug('Version check skipped:', err.message);
    }
  }, []);

  // Force refresh stale content when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdates();
      }
    };

    // Check on initial load and when tab becomes visible
    checkForUpdates();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Periodic check
    const versionInterval = setInterval(checkForUpdates, VERSION_CHECK_INTERVAL);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(versionInterval);
    };
  }, [checkForUpdates]);

  useEffect(() => {
    // Check for logged in user (try both keys for compatibility)
    const userData = localStorage.getItem('vastu_user') || localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error('Failed to parse user data');
      }
    }

    // Listen for login/logout events
    const handleStorageChange = () => {
      const userData = localStorage.getItem('vastu_user') || localStorage.getItem('user');
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (e) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLogin', handleStorageChange);
    window.addEventListener('userLogout', handleStorageChange);
    
    // Also check on interval for same-tab updates
    const interval = setInterval(handleStorageChange, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogin', handleStorageChange);
      window.removeEventListener('userLogout', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  return (
    <HelmetProvider>
    <div className="App">
      {/* Update Available Banner */}
      {updateAvailable && (
        <div 
          className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 flex items-center justify-center gap-3 shadow-lg cursor-pointer animate-pulse"
          onClick={forceRefresh}
        >
          <span className="text-xl">🔄</span>
          <span className="font-medium hidden sm:inline">New update available! Click here to refresh and get the latest features.</span>
          <span className="font-medium sm:hidden">Update available!</span>
          <button 
            className="ml-2 px-4 py-1 bg-white text-blue-600 rounded-full font-bold text-sm hover:bg-blue-50 transition"
            onClick={(e) => {
              e.stopPropagation();
              forceRefresh();
            }}
          >
            Update Now
          </button>
        </div>
      )}
      
      <BrowserRouter>
        <RouteTracker />
        <GlobalNav />
        <MobileAppShell>
          <ErrorBoundary>
          <Suspense fallback={
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: 40, height: 40, border: '3px solid #e5e7eb', borderTopColor: '#4A5D4F', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                <p style={{ color: '#6b7280', fontSize: 14 }}>Loading...</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
              </div>
            </div>
          }>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/print/report/:token" element={<PrintReportPage />} />
            <Route path="/portal/:token" element={<ClientPortalPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/compass" element={<CompassPage />} />
            <Route path="/draw" element={<FloorPlanCreatorPage />} />
            <Route path="/astro-vastu" element={<AstroVastuPage />} />
            <Route path="/pinnacle-guru" element={<PinnacleGuruPage />} />
            {/* Redirect old chatbot routes to unified Pinnacle Guru */}
            <Route path="/astro-guru" element={<Navigate to="/pinnacle-guru" replace />} />
            <Route path="/vastu-guru" element={<Navigate to="/pinnacle-guru" replace />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/consultation" element={<ConsultationLandingPage />} />
            <Route path="/consultation-success" element={<ConsultationSuccessPage />} />
            <Route path="/remedies" element={<RemediesPage />} />
            <Route path="/remedies/:slug" element={<RemediesPage />} />
            <Route path="/tutorials" element={<TutorialsPage />} />
            <Route path="/go-premium" element={<GoPremiumPage />} />
            <Route path="/premium" element={<PremiumPage />} />
            {/* Free occult tools */}
            <Route path="/tools/mangal-dosha" element={<MangalDoshaToolPage />} />
            <Route path="/tools/kalsarpa-dosha" element={<KalsarpaToolPage />} />
            <Route path="/tools/sade-sati" element={<SadeSatiToolPage />} />
            <Route path="/tools/tithi" element={<TithiToolPage />} />
            <Route path="/tools/nakshatra-finder" element={<NakshatraFinderPage />} />
            <Route path="/tools/muhurat" element={<MuhuratFinderPage />} />
            <Route path="/tools/kundli-milan" element={<KundliMilanPage />} />
            <Route path="/tools/baby-name" element={<BabyNamePage />} />
            <Route path="/tools/marriage-dates" element={<MarriageDatesPage />} />
            <Route path="/family-vault" element={<FamilyVaultPage />} />
            <Route path="/friends-compare" element={<FriendsComparePage />} />
            {/* Phase C tools */}
            <Route path="/tools/tarot" element={<TarotPage />} />
            <Route path="/tools/tarot/journal" element={<TarotJournalPage />} />
            <Route path="/tools/tarot/shared/:shareToken" element={<TarotSharedPage />} />
            <Route path="/tools/lal-kitab" element={<LalKitabPage />} />
            <Route path="/tools/name-audit" element={<NameNumerologyAuditPage />} />
            <Route path="/tools/vehicle-number" element={<VehicleNumeroPage />} />
            <Route path="/tools/rudraksha" element={<RudrakshaPage />} />
            {/* Courses */}
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/:handle" element={<CourseDetailPage />} />
            <Route path="/my-courses" element={<MyCoursesPage />} />
            {/* OK Community */}
            <Route path="/community" element={<CommunityFeedPage />} />
            <Route path="/community/ask" element={<CommunityAskPage />} />
            <Route path="/community/feed" element={<CommunitySocialFeedPage />} />
            <Route path="/community/leaderboard" element={<CommunityLeaderboardPage />} />
            <Route path="/community/practitioners" element={<CommunityDirectoryPage type="practitioners" />} />
            <Route path="/community/acharyas" element={<CommunityDirectoryPage type="acharyas" />} />
            <Route path="/community/u/:userId" element={<CommunityProfilePage />} />
            <Route path="/community/:postId" element={<CommunityPostPage />} />
            {/* Premium extras */}
            <Route path="/premium/muhurat-calendar" element={<MuhuratCalendarPage />} />
            <Route path="/premium/karma-log" element={<KarmaLogPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/mahakundli" element={<MahakundliLandingPage />} />
            <Route path="/lalkitab" element={<LalKitabLandingPage />} />
            <Route path="/bnn" element={<BNNLandingPage />} />
            <Route path="/raj-yog" element={<RajYogLandingPage />} />
            <Route path="/life-report" element={<LifeReportLandingPage />} />
            <Route path="/payment/return" element={<PaymentReturnPage />} />
            <Route path="/pay-continue" element={<PayContinuePage />} />
            <Route path="/resume/:type/:orderId" element={<ResumeCheckoutPage />} />
            <Route path="/astro-report" element={<AstroReportLandingPage />} />
            <Route path="/vastu-report" element={<VastuReportLandingPage />} />
            <Route path="/numerology" element={<NumerologyPage />} />
            <Route path="/numerology-report" element={<NumerologyReportLandingPage />} />
            <Route path="/numerology/report/:reportId" element={<NumerologyReportPage />} />
            {/* Redirect /kundli to dashboard Kundlis tab */}
            <Route path="/kundli" element={<Navigate to="/dashboard?tab=kundlis" replace />} />
            <Route path="/editor" element={<EditorPage />} />
            <Route path="/editor/:projectId" element={<EditorPage />} />
            <Route path="/vastu-report/start" element={<VastuReportGatePage />} />
            <Route path="/astro-vastu-report" element={<AstroVastuReportLandingPage />} />
            <Route path="/palmistry-report" element={<PalmistryReportLandingPage />} />
            <Route path="/face-reading-report" element={<FaceReadingReportLandingPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/install" element={<InstallPage />} />
            <Route path="/get-app" element={<GetAppPage />} />
            <Route path="/client-crm" element={<ClientCRMPage />} />
            <Route path="/client-crm/:clientId" element={<ClientProfilePage />} />
            <Route path="/client-crm/:clientId/report/:reportId" element={<CRMReportViewerPage />} />
            
            {/* SEO Landing Pages */}
            <Route path="/vastu-course" element={<VastuCoursePage />} />
            <Route path="/vastu-software" element={<VastuSoftwarePage />} />
            <Route path="/45-devtas-vastu" element={<DevtasVastuPage />} />
            <Route path="/acharya-vikul-bansal" element={<AcharyaProfilePage />} />
            <Route path="/vastu-consultation" element={<VastuConsultationPage />} />
            
            {/* Blog & SEO Manager */}
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPage />} />
            <Route path="/seo-manager" element={<SEOBlogManager />} />
            
            {/* Free Tools */}
            <Route path="/tao-aay-calculator" element={<TaoAayCalculator />} />
            <Route path="/vastu-calculators" element={<VastuCalculators />} />
            
            {/* SEO Landing Pages - Keyword Targeted */}
            <Route path="/free-vastu-software" element={<FreeVastuSoftwarePage />} />
            <Route path="/kp-astrology-software" element={<KPAstrologySoftwarePage />} />
            <Route path="/free-numerology-calculator" element={<FreeNumerologyCalculatorPage />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </MobileAppShell>

        {/* Floating Pinnacle Guru AI Chat - Only on desktop, mobile uses bottom nav.
            Rendered inside BrowserRouter so it can hide itself on product pages. */}
        <div className="hidden md:block">
          <FloatingGuruChat userId={user?.id} isLoggedIn={!!user} />
        </div>
      </BrowserRouter>
      <Toaster position="top-center" className="sm:bottom-right" />
    </div>
    </HelmetProvider>
  );
}

// Floating Pinnacle Guru AI Chat Component
function FloatingGuruChat({ userId, isLoggedIn }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [sessionId, setSessionId] = React.useState(null);
  const messagesEndRef = React.useRef(null);
  const inputRef = React.useRef(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  React.useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    
    if (!isLoggedIn) {
      toast.error('Please login to chat with Ask OK');
      window.location.href = '/login';
      return;
    }

    const userMessage = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/pinnacle-guru/chat?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage.content,
          session_id: sessionId,
          kundli_id: null
        })
      });

      // Check if response is ok first
      if (!res.ok) {
        const errorText = await res.text();
        console.error("API Error:", res.status, errorText);
        toast.error(`Server error: ${res.status}`);
        return;
      }

      // Parse JSON response with error handling
      let data;
      try {
        const responseText = await res.text();
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("JSON Parse error:", parseError);
        toast.error("Failed to parse server response");
        return;
      }

      if (data.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
        setSessionId(data.session_id);
      } else {
        const errorMsg = data.message || 'Something went wrong';
        toast.error(errorMsg);
        if (data.error === 'subscription_required') {
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: '🙏 Your free trial has ended. Please subscribe to Premium for unlimited questions!' 
          }]);
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Sample questions
  const sampleQuestions = [
    "What is my Mulank based on my DOB?",
    "Best direction for main entrance?",
    "Which gemstone should I wear?",
    "Remedy for kitchen in North-East?"
  ];

  // Hide the Ask OK widget on Remedies product detail pages (/remedies/:slug)
  // so FB/Insta ad traffic stays focused on purchasing. Grid (/remedies) keeps it.
  if (/^\/remedies\/.+/.test(location.pathname)) return null;
  // Public no-auth pages (client portal, print capture) get no consultant chat widget.
  if (location.pathname.startsWith("/portal/") || location.pathname.startsWith("/print/")) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Chat Box */}
      {isOpen && (
        <div className="absolute bottom-20 right-0 w-[380px] h-[500px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-slideUp">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white">
                  <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Ask OK</h3>
                <p className="text-white/70 text-xs">Vastu • Astro • Numerology</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => window.location.href = '/pinnacle-guru'}
                className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                title="Open Full Page"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                </svg>
              </button>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
            {messages.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500 text-sm mb-3">Ask me anything about Vastu, Astrology, or Numerology!</p>
                <div className="space-y-2">
                  {sampleQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => { setInput(q); inputRef.current?.focus(); }}
                      className="block w-full text-left px-3 py-2 bg-white rounded-lg text-xs text-gray-600 hover:bg-amber-50 hover:text-amber-700 transition-colors border border-gray-100"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                    msg.role === 'user' 
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' 
                      : 'bg-white text-gray-700 border border-gray-100'
                  }`}>
                    {msg.content.split('\n').map((line, j) => (
                      <p key={j} className={j > 0 ? 'mt-1' : ''}>{line}</p>
                    ))}
                  </div>
                </div>
              ))
            )}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-xl px-3 py-2 border border-gray-100">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                    <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                    <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-3 bg-white border-t border-gray-100">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={isLoggedIn ? "Ask a question..." : "Login to chat..."}
                disabled={!isLoggedIn || isLoading}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:bg-gray-100"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading || !isLoggedIn}
                className="px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:from-amber-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                </svg>
              </button>
            </div>
            {!isLoggedIn && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                <a href="/login" className="text-amber-600 hover:underline">Login</a> to start chatting
              </p>
            )}
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`group relative flex items-center justify-center w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 ${
          isOpen 
            ? 'bg-gray-700 hover:bg-gray-800' 
            : 'bg-gradient-to-r from-amber-500 to-orange-600'
        }`}
        data-testid="floating-guru-button"
      >
        {isOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/>
            <path d="M5 19l1 3 1-3 3-1-3-1-1-3-1 3-3 1z"/>
            <path d="M19 12l1 2 1-2 2-1-2-1-1-2-1 2-2 1z"/>
          </svg>
        )}
        
        {/* Tooltip */}
        {!isOpen && (
          <span className={`absolute right-full mr-3 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            Ask OK
            <span className="absolute right-0 top-1/2 transform translate-x-1 -translate-y-1/2 border-4 border-transparent border-l-gray-900"></span>
          </span>
        )}
        
        {/* Pulse animation */}
        {!isOpen && <span className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-20"></span>}
      </button>
    </div>
  );
}

export default App;
